import {
  ComponentPropsWithoutRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import * as styles from "./ModalForm.css";
import * as Dialog from "@radix-ui/react-dialog";

import {
  CardHeadingText,
  CardHeadingTextProps,
} from "../components/CardHeadingText";
import { Button, ButtonProps } from "../elements/Button";
// @ts-ignore
import { Transition } from "react-transition-group";
import { gsap } from "gsap";
import { useForm, useFormState } from "react-hook-form";
import { Paragraph } from "../elements/Paragraph";
import { ModalContext } from "~/lib/context/ModalContext";
import { soundModalPostive } from "~/lib/hooks/sounds/soundModalPositive";
import { soundModalNegative } from "~/lib/hooks/sounds/soundModalNegative";
import { soundButtonDisabled } from "~/lib/hooks/sounds/soundButtonDisabled";
import { useSound } from "~/lib/hooks/sounds/useSound";
import { useGSAP } from "@gsap/react";

export type ModalFormProps = {
  buttons?: ButtonProps[];
  alert: CardHeadingTextProps;
  asModal?: boolean;
  open?: boolean;
  onClose?: () => void;
  setIsModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  handleSubmit?: () => Promise<void>;
  handleAnswer?: ButtonProps["onClick"];
  submitted?: boolean;
  hasBackgroundFade?: boolean;
  voiceOverSrc?: string;
  containerRef?: React.MutableRefObject<HTMLDivElement | null>;
  hasSound?: boolean;
} & ComponentPropsWithoutRef<"div">;

export const ModalForm = ({
  buttons,
  children,
  asModal = true,
  alert,
  open = false,
  onClose,
  setIsModalOpen,
  handleSubmit,
  handleAnswer,
  submitted = false,
  hasBackgroundFade = false,
  voiceOverSrc,
  containerRef,
  hasSound = true,
  ...props
}: ModalFormProps) => {
  let [isOpen, setIsOpen] = useState(false);
  const nodeRef = useRef(null);
  const panelRef = useRef(null);
  const backgroundRef = useRef(null);
  const overlayRef = useRef(null);

  const voiceOverPlayer = useRef(voiceOverSrc && useSound(voiceOverSrc));

  useEffect(() => {
    setIsOpen(open);
    voiceOverPlayer.current && voiceOverPlayer.current.stop();
  }, []);

  const onEnter = () => {
    useGSAP(() => {
      const tl = gsap.timeline({
        defaults: { ease: "elastic.out(1,1)", duration: "0.8" },
        onStart: () => {
          setIsModalOpen && setIsModalOpen(true);
        },
        onComplete: () => {
          voiceOverPlayer.current &&
            !voiceOverPlayer.current.playing() &&
            voiceOverPlayer.current.play();
        },
      });

      panelRef.current &&
        tl.fromTo(panelRef.current, { scale: 0 }, { scale: 1 }, "+0.5");
      panelRef.current &&
        tl.fromTo(
          backgroundRef.current,
          { scale: 0, opacity: 0 },
          { scale: 1, opacity: 1 },
          "<"
        );
      isOpen &&
        tl.call(
          () => {
            alert.theme === "positive"
              ? hasSound && soundModalPostive()
              : hasSound && alert.theme === "negative" && soundModalNegative();
          },
          [],
          "<"
        );
      hasBackgroundFade &&
        overlayRef.current &&
        tl.fromTo(
          overlayRef.current,
          { backgroundColor: "rgba(255, 255, 255, 0)" },
          { backgroundColor: "rgba(255, 255, 255, 0.5)" },
          "<"
        );
    }, [isOpen]);
  };

  const onExit = (
    buttonEvent?: React.MouseEventHandler<HTMLButtonElement> | undefined
  ) => {
    const tl = gsap.timeline({
      defaults: { ease: "elastic.in(1,1)", duration: "0.8" },
      onStart: () => {
        voiceOverPlayer.current && voiceOverPlayer.current.stop();
      },
      onComplete: (e) => {
        setIsModalOpen && setIsModalOpen(false), buttonEvent && buttonEvent(e);
        setIsOpen(false);
      },
    });

    tl.fromTo(panelRef.current, { scale: 1 }, { scale: 0 }),
      tl.fromTo(
        backgroundRef.current,
        { scale: 1, opacity: 1 },
        { scale: 0, opacity: 0 },
        "<"
      );
    hasBackgroundFade &&
      tl.fromTo(
        overlayRef.current,
        { backgroundColor: "rgba(255, 255, 255, 0.5)" },
        { backgroundColor: "rgba(255, 255, 255, 0)" },
        "<"
      );
  };

  const dialogContent = () => {
    return (
      <div
        className={styles.dialogOverlay({ hasBackground: hasBackgroundFade })}
        {...props}
        ref={overlayRef}
      >
        <Dialog.Overlay
          forceMount
          onClick={() => asModal && soundButtonDisabled()}
          className={styles.clickOverlay}
        ></Dialog.Overlay>

        <div className={styles.gradientBackground} ref={backgroundRef}></div>
        <Dialog.Content
          forceMount
          className={styles.dialogPanel}
          ref={panelRef}
          aria-describedby={undefined}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Dialog.Title>
            <CardHeadingText {...alert}></CardHeadingText>
          </Dialog.Title>
          <div className={styles.buttons}>
            {buttons &&
              buttons.map((button, index) => {
                return (
                  <Button
                    {...button}
                    key={index}
                    width="full"
                    theme={button.theme ? button.theme : "dark"}
                    onClick={async () => {
                      handleSubmit
                        ? await handleSubmit()
                        : onExit(button.onClick);
                    }}
                  >
                    <Paragraph>{button.children}</Paragraph>
                  </Button>
                );
              })}
          </div>
        </Dialog.Content>
      </div>
    );
  };

  return (
    <>
      {children && (
        <div
          className={styles.trigger}
          onClick={() => {
            setIsOpen(true);
          }}
        >
          {children}
        </div>
      )}
      <Transition
        nodeRef={nodeRef}
        in={isOpen}
        onEnter={onEnter()}
        timeout={1000}
        appear
      >
        {(state: any) => {
          // console.log(state);
          return (
            <div ref={nodeRef}>
              <Dialog.Root open={isOpen} modal={asModal}>
                {asModal ? (
                  <Dialog.Portal
                    forceMount
                    container={containerRef && containerRef.current}
                  >
                    {dialogContent()}
                  </Dialog.Portal>
                ) : (
                  dialogContent()
                )}
              </Dialog.Root>
            </div>
          );
        }}
      </Transition>
    </>
  );
};
